import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  CheckCircle,
} from "lucide-react";
import emailjs from "@emailjs/browser";

export default function EnrollModal({
  open,
  onClose,
  course = "React JS Development",
}) {
  const formRef = useRef();

  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSending(true);
    setMessage("");

    try {
      await emailjs.sendForm(
        "service_ecfbcpi",
        "template_g4gf1p7",
        formRef.current,
        {
          publicKey: "1KCrv-bR7fKaqZz-D",
        }
      );

      setMessage("Registration successful! We will contact you soon.");

      // Reset form
      formRef.current.reset();

      // Close popup after 2 seconds
      setTimeout(() => {
        onClose();
        setMessage("");
      }, 2000);

    } catch (error) {
      console.error("EmailJS Error:", error);

      setMessage(
        "Something went wrong. Please try again."
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.95,
              y: 20,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              scale: 0.95,
              y: 20,
            }}
            transition={{ duration: 0.3 }}
            className="relative bg-white w-full max-w-2xl rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto"
          >

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="absolute top-5 right-5 z-10 text-gray-500 hover:text-black transition"
            >
              <X size={26} />
            </button>

            {/* Content */}
            <div className="px-6 py-7 md:px-10 md:py-8">

              {/* Heading */}
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Enroll in this Course
              </h2>

              <p className="text-gray-500 mt-2 mb-7">
                Take the first step towards your bright future
              </p>

              {/* Form */}
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="space-y-4"
              >

                {/* Course - hidden */}
                <input
                  type="hidden"
                  name="course"
                  value={course}
                />

                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>

                  <div className="relative">
                    <User
                      size={20}
                      className="absolute left-4 top-3 text-gray-400"
                    />

                    <input
                      type="text"
                      name="name"
                      placeholder="Enter your full name"
                      required
                      autoComplete="name"
                      className="w-full border border-gray-300 rounded-xl pl-11 pr-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>

                  <div className="relative">
                    <Mail
                      size={20}
                      className="absolute left-4 top-3 text-gray-400"
                    />

                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email address"
                      required
                      autoComplete="email"
                      className="w-full border border-gray-300 rounded-xl pl-11 pr-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                    />
                  </div>
                </div>

                {/* Mobile */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile Number
                  </label>

                  <div className="relative">
                    <Phone
                      size={20}
                      className="absolute left-4 top-3 text-gray-400"
                    />

                    <input
                      type="tel"
                      name="mobile"
                      placeholder="Enter your mobile number"
                      required
                      autoComplete="tel"
                      className="w-full border border-gray-300 rounded-xl pl-11 pr-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                    />
                  </div>
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>

                  <div className="relative">
                    <MapPin
                      size={20}
                      className="absolute left-4 top-3 text-gray-400"
                    />

                    <input
                      type="text"
                      name="city"
                      placeholder="Enter your city"
                      required
                      autoComplete="address-level2"
                      className="w-full border border-gray-300 rounded-xl pl-11 pr-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                    />
                  </div>
                </div>

                {/* Terms */}
                <label className="flex items-center gap-3 text-sm text-gray-600 cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    name="agree"
                    value="Yes"
                    required
                    className="w-4 h-4 accent-blue-600"
                  />

                  <span>
                    I agree to the{" "}
                    <span className="text-blue-600 font-medium">
                      Terms & Conditions
                    </span>
                  </span>
                </label>

                {/* Success / Error Message */}
                {message && (
                  <div
                    className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                      message.includes("successful")
                        ? "bg-green-50 text-green-700"
                        : "bg-red-50 text-red-600"
                    }`}
                  >
                    {message.includes("successful") && (
                      <CheckCircle size={18} />
                    )}

                    {message}
                  </div>
                )}

                {/* Register */}
                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full py-3.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSending ? "Sending..." : "Register →"}
                </button>

              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}